import { useEffect, useState } from 'react';

const STORAGE_KEY = 'explore-learning-progress-v1';

/** @typedef {{ score: number, lastSeenAt: string, dueAt: string, reviewCount: number }} MasteryRecord */

/** @typedef {{ completedLessons: string[], startedLessons: string[], activityScores: Record<string, object>, lastVisitedWorld: string | null, completedAt: Record<string, string>, guidedLessonProgress: Record<string, { stepIndex: number, activityStepsDone: Record<string, boolean> }>, lessonMastery: Record<string, MasteryRecord> }} ProgressState */

// Spaced-review intervals (days). Brilliant-style "review when it matters",
// no streaks, no XP, no daily quotas — purely a memory aid.
const REVIEW_INTERVALS_DAYS = [1, 3, 7, 14, 30];
const DAY_MS = 24 * 60 * 60 * 1000;
const MASTERY_PASS_THRESHOLD = 0.7;

const listeners = new Set();

function emit() {
  listeners.forEach((fn) => fn());
}

/**
 * @returns {ProgressState}
 */
function defaultState() {
  return {
    completedLessons: [],
    startedLessons: [],
    activityScores: {},
    lastVisitedWorld: null,
    completedAt: {},
    guidedLessonProgress: {},
    lessonMastery: {},
  };
}

/**
 * @param {unknown} raw
 * @returns {ProgressState}
 */
function normalize(raw) {
  const d = defaultState();
  if (!raw || typeof raw !== 'object') return d;
  const o = /** @type {Record<string, unknown>} */ (raw);
  return {
    completedLessons: Array.isArray(o.completedLessons) ? o.completedLessons.map(String) : d.completedLessons,
    startedLessons: Array.isArray(o.startedLessons) ? o.startedLessons.map(String) : d.startedLessons,
    activityScores:
      o.activityScores && typeof o.activityScores === 'object' && !Array.isArray(o.activityScores)
        ? /** @type {Record<string, object>} */ (o.activityScores)
        : d.activityScores,
    lastVisitedWorld:
      typeof o.lastVisitedWorld === 'string' ? o.lastVisitedWorld : o.lastVisitedWorld === null ? null : d.lastVisitedWorld,
    completedAt:
      o.completedAt && typeof o.completedAt === 'object' && !Array.isArray(o.completedAt)
        ? /** @type {Record<string, string>} */ (o.completedAt)
        : d.completedAt,
    guidedLessonProgress:
      o.guidedLessonProgress && typeof o.guidedLessonProgress === 'object' && !Array.isArray(o.guidedLessonProgress)
        ? /** @type {Record<string, { stepIndex: number, activityStepsDone: Record<string, boolean> }>} */ (
            o.guidedLessonProgress
          )
        : d.guidedLessonProgress,
    lessonMastery:
      o.lessonMastery && typeof o.lessonMastery === 'object' && !Array.isArray(o.lessonMastery)
        ? /** @type {Record<string, MasteryRecord>} */ (o.lessonMastery)
        : d.lessonMastery,
  };
}

/**
 * @returns {ProgressState}
 */
export function loadProgress() {
  if (typeof window === 'undefined') return defaultState();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    return normalize(parsed);
  } catch {
    return defaultState();
  }
}

/**
 * @param {ProgressState} next
 */
function persist(next) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // quota / private mode — ignore
  }
  emit();
}

/**
 * @param {(state: ProgressState) => ProgressState} updater
 */
function update(updater) {
  const prev = loadProgress();
  const next = updater(prev);
  persist(next);
}

export function subscribeProgress(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/** @param {string} worldId @param {string} pathId */
export function makeLessonKey(worldId, pathId) {
  return `${worldId}:${pathId}`;
}

/**
 * Compute the next review timestamp using SM-2-lite spacing.
 * - Failures (score < threshold) reset the schedule to 1 day from now.
 * - Successes step through [1, 3, 7, 14, 30] days based on prior reviewCount.
 * @param {number} score 0..1
 * @param {MasteryRecord | null | undefined} prev
 * @param {number} nowMs
 * @returns {{ dueAtMs: number, reviewCount: number }}
 */
function computeNextDue(score, prev, nowMs) {
  const passed = (Number(score) || 0) >= MASTERY_PASS_THRESHOLD;
  const prevCount = Math.max(0, Number(prev?.reviewCount) || 0);
  if (!passed) {
    return { dueAtMs: nowMs + DAY_MS, reviewCount: 0 };
  }
  const idx = Math.min(prevCount, REVIEW_INTERVALS_DAYS.length - 1);
  return {
    dueAtMs: nowMs + REVIEW_INTERVALS_DAYS[idx] * DAY_MS,
    reviewCount: prevCount + 1,
  };
}

/**
 * @param {string} lessonKey
 * @param {Record<string, unknown>} scorePayload from engine onComplete (normalized extras)
 */
export function markLessonComplete(lessonKey, scorePayload = {}) {
  const now = new Date();
  const ts = now.toISOString();
  const score = typeof scorePayload?.score === 'number'
    ? scorePayload.score
    : scorePayload?.guidedLesson
      ? 1
      : 1;
  update((s) => {
    const completedLessons = s.completedLessons.includes(lessonKey)
      ? [...s.completedLessons]
      : [...s.completedLessons, lessonKey];
    const prevMastery = s.lessonMastery?.[lessonKey] || null;
    const { dueAtMs, reviewCount } = computeNextDue(score, prevMastery, now.getTime());
    const mastery = {
      score,
      lastSeenAt: ts,
      dueAt: new Date(dueAtMs).toISOString(),
      reviewCount,
    };
    return {
      ...s,
      completedLessons,
      activityScores: { ...s.activityScores, [lessonKey]: { ...scorePayload, updatedAt: ts } },
      completedAt: { ...s.completedAt, [lessonKey]: ts },
      lessonMastery: { ...s.lessonMastery, [lessonKey]: mastery },
    };
  });
}

/** @param {string} lessonKey */
export function markLessonStarted(lessonKey) {
  update((s) => {
    if (s.startedLessons.includes(lessonKey)) return s;
    return { ...s, startedLessons: [...s.startedLessons, lessonKey] };
  });
}

/** @param {string} worldId */
export function setLastVisitedWorld(worldId) {
  if (!worldId) return;
  update((s) => ({ ...s, lastVisitedWorld: worldId }));
}

/** @param {string} lessonKey */
export function isLessonCompleted(lessonKey) {
  return loadProgress().completedLessons.includes(lessonKey);
}

/** @param {string} lessonKey */
export function getGuidedLessonProgress(lessonKey) {
  return loadProgress().guidedLessonProgress[lessonKey] ?? null;
}

/**
 * @param {string} lessonKey
 * @param {{ stepIndex: number, activityStepsDone: Record<string, boolean> }} payload
 */
export function saveGuidedLessonProgress(lessonKey, payload) {
  update((s) => ({
    ...s,
    guidedLessonProgress: {
      ...s.guidedLessonProgress,
      [lessonKey]: { ...payload },
    },
  }));
}

/** @param {string} lessonKey */
export function clearGuidedLessonProgress(lessonKey) {
  update((s) => {
    const next = { ...s.guidedLessonProgress };
    delete next[lessonKey];
    return { ...s, guidedLessonProgress: next };
  });
}

/**
 * Allow replay: drop completion and in-lesson checkpoints.
 * @param {string} lessonKey
 */
export function clearLessonCompletionForReplay(lessonKey) {
  update((s) => {
    const nextProg = { ...s.guidedLessonProgress };
    delete nextProg[lessonKey];
    const completedLessons = s.completedLessons.filter((k) => k !== lessonKey);
    const completedAt = { ...s.completedAt };
    delete completedAt[lessonKey];
    const activityScores = { ...s.activityScores };
    delete activityScores[lessonKey];
    const lessonMastery = { ...s.lessonMastery };
    delete lessonMastery[lessonKey];
    return {
      ...s,
      completedLessons,
      completedAt,
      activityScores,
      guidedLessonProgress: nextProg,
      lessonMastery,
    };
  });
}

/** @param {string} lessonKey */
export function getLessonMastery(lessonKey) {
  return loadProgress().lessonMastery[lessonKey] || null;
}

/**
 * Lessons whose review window has elapsed. Sorted by oldest dueAt first.
 * @param {number} [nowMs] defaults to Date.now()
 * @returns {Array<{ lessonKey: string, mastery: MasteryRecord }>}
 */
export function getReviewQueue(nowMs = Date.now()) {
  const { lessonMastery } = loadProgress();
  return Object.entries(lessonMastery)
    .filter(([, m]) => m && Date.parse(m.dueAt) <= nowMs)
    .map(([lessonKey, mastery]) => ({ lessonKey, mastery }))
    .sort((a, b) => Date.parse(a.mastery.dueAt) - Date.parse(b.mastery.dueAt));
}

/**
 * Live-updating review queue. Re-evaluates whenever progress changes.
 */
export function useReviewQueue() {
  const [queue, setQueue] = useState(() => getReviewQueue());
  useEffect(() => subscribeProgress(() => setQueue(getReviewQueue())), []);
  return queue;
}

/**
 * Normalize engine onComplete detail for storage.
 * @param {Record<string, unknown>} detail
 */
export function buildScoreFromEngineDetail(detail) {
  const engine = String(detail.engine || '');
  if (engine === 'multiple_choice') {
    const total = Math.max(1, Number(detail.totalQuestions) || 1);
    const correct = Math.min(total, Math.max(0, Number(detail.correctCount) || 0));
    const helped = Math.min(correct, Math.max(0, Number(detail.helpedCount) || 0));
    return {
      engine,
      score: correct / total,
      correctCount: correct,
      helpedCount: helped,
      totalQuestions: total,
    };
  }
  if (engine === 'tap_on_image' || engine === 'canvas_manipulative' || engine === 'simulation_scrub') {
    const misses = Math.max(0, Number(detail.misses ?? detail.attempts ?? 0));
    return {
      engine,
      score: detail.perfect ? 1 : Math.max(0, 1 - misses * 0.1),
      perfect: !!detail.perfect,
      misses,
    };
  }
  if (engine === 'exploration_card') {
    const n = Math.max(0, Number(detail.cardsViewed) || 0);
    return { engine, score: 1, cardsViewed: n };
  }
  if (engine === 'sequence' || engine === 'match' || engine === 'pattern') {
    const perfect = !!detail.perfect;
    return { engine, score: perfect ? 1 : 0, perfect };
  }
  return { engine: engine || 'unknown', score: 0 };
}

export function useProgressSnapshot() {
  const [snap, setSnap] = useState(() => loadProgress());
  useEffect(() => subscribeProgress(() => setSnap(loadProgress())), []);
  return snap;
}
